import { BRAND, BRAND_ASSETS } from '@/constants/brand'

/**
 * Знак с названием системы.
 *
 * В УЗКИХ РАЗМЕРАХ НАЗВАНИЕ СОКРАЩЁННОЕ, А НЕ ОБРЕЗАННОЕ. «Ситуационный центр
 * Атырау» длиннее прежнего «Цифрового мониторинга» и в шапке упиралось в
 * многоточие — читалось «Ситуационный центр Аты…». Многоточие в названии
 * системы выглядит как сбой загрузки, поэтому в компактных размерах идёт короткая
 * форма (`systemTitle`), а полная остаётся там, где под неё есть место.
 */

type BrandMarkSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl'

/** Размеры, в которых полное название не укладывается в одну строку. */
const COMPACT_TITLE_SIZES: ReadonlySet<BrandMarkSize> = new Set<BrandMarkSize>([
  'sm',
  'md',
])

type BrandMarkProps = {
  variant?: 'light' | 'dark'
  showPanelTitle?: boolean
  showTagline?: boolean
  /** @deprecated используйте size="sm" */
  compact?: boolean
  size?: BrandMarkSize
  className?: string
}

const sizeStyles: Record<
  BrandMarkSize,
  {
    logo: string
    title: string
    subtitle: string
    tagline: string
    gap: string
    textLeading: string
    subtitleMt: string
    taglineMt: string
  }
> = {
  sm: {
    logo: 'h-8 w-auto',
    title: 'text-sm',
    subtitle: 'text-xs',
    tagline: 'text-[10px]',
    gap: 'gap-2.5',
    textLeading: 'leading-snug',
    subtitleMt: 'mt-0.5',
    taglineMt: 'mt-1',
  },
  md: {
    logo: 'h-10 ipad:h-11 w-auto',
    title: 'text-base ipad:text-lg',
    subtitle: 'text-xs ipad:text-sm',
    tagline: 'text-xs',
    gap: 'gap-3',
    textLeading: 'leading-snug',
    subtitleMt: 'mt-0.5',
    taglineMt: 'mt-1',
  },
  lg: {
    logo: 'h-12 ipad:h-14 w-auto',
    title: 'text-lg ipad:text-xl',
    subtitle: 'text-sm ipad:text-base',
    tagline: 'text-xs ipad:text-sm',
    gap: 'gap-3.5 ipad:gap-4',
    textLeading: 'leading-snug',
    subtitleMt: 'mt-0.5',
    taglineMt: 'mt-1',
  },
  xl: {
    logo: 'h-14 ipad:h-16 w-auto',
    title: 'text-lg ipad:text-xl',
    subtitle: 'text-sm ipad:text-base',
    tagline: 'text-xs',
    gap: 'gap-4 ipad:gap-4',
    textLeading: 'leading-snug',
    subtitleMt: 'mt-0.5',
    taglineMt: 'mt-1',
  },
  '2xl': {
    logo: 'h-[3.75rem] ipad:h-[4.5rem] w-auto',
    title: 'text-xl ipad:text-2xl',
    subtitle: 'text-sm ipad:text-base',
    tagline: 'text-xs ipad:text-sm',
    gap: 'gap-4 ipad:gap-5',
    textLeading: 'leading-[1.15]',
    subtitleMt: 'mt-0',
    taglineMt: 'mt-0.5',
  },
}

export function BrandMark({
  variant = 'dark',
  showPanelTitle = false,
  showTagline = false,
  compact = false,
  size,
  className = '',
}: BrandMarkProps) {
  const resolvedSize: BrandMarkSize = size ?? (compact ? 'sm' : 'md')
  const styles = sizeStyles[resolvedSize]
  const isLight = variant === 'light'
  /*
   * ЛОГОТИП — ЛИНИЯМИ, КАК НА ВЕБЕ, И НА СВЕТЛОМ ФОНЕ ТОЖЕ. Прежде тёмный вариант
   * ставил картинку на синей подложке (`logo`), а веб в светлой теме берёт те же
   * белые линии и затемняет их фильтром (`ScCityLogo`, `brightness(0.22)`).
   */
  const logoSrc = BRAND_ASSETS.logoOnDark
  const isCompactTitle = COMPACT_TITLE_SIZES.has(resolvedSize)
  const titleText = isCompactTitle ? BRAND.systemTitle : BRAND.productName

  return (
    <div className={`flex items-center min-w-0 ${styles.gap} ${className}`}>
      <img
        src={logoSrc}
        alt={BRAND.productName}
        className={`${styles.logo} shrink-0 object-contain`}
        style={isLight ? undefined : { filter: 'brightness(0.22)' }}
      />
      <div className={`min-w-0 text-left ${styles.textLeading}`}>
        <p
          className={`font-bold tracking-tight text-left ${
            // Короткая форма — в одну строку с обрезкой; полная — перенос по словам,
            // без text-balance: он визуально центрирует строки.
            isCompactTitle ? 'truncate' : ''
          } ${styles.title} ${isLight ? 'text-white' : 'text-cn-text'}`}
        >
          {titleText}
        </p>
        {showPanelTitle && (
          <p
            className={`truncate text-left ${styles.subtitleMt} ${styles.subtitle} ${
              isLight ? 'text-white/90' : 'text-cn-primary font-medium'
            }`}
          >
            {BRAND.panelTitle}
          </p>
        )}
        {showTagline && (
          <p
            className={`${styles.taglineMt} whitespace-pre-line text-left ${styles.tagline} ${
              isLight ? 'text-white/75' : 'text-cn-text-secondary'
            }`}
          >
            {BRAND.tagline}
          </p>
        )}
      </div>
    </div>
  )
}
